/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { ApolloProvider } from "@apollo/client";
import dynamic from "next/dynamic";
import "@aqar-app/aqar-design-system/lib/index.css";
import "leaflet/dist/leaflet.css";
import "leaflet-gesture-handling/dist/leaflet-gesture-handling.css";
import { useApollo } from "../lib/apolloClient";
import { AhliCalculatorProvider } from "../contexts/ahli-calculator/ahliCalculator.provider";
import { ListingListProvider } from "../contexts/listingsList/listingsList.provider";
import { RegionsProvider } from "../contexts/regions/regions.provider";
import { AuthProvider } from "../contexts/auth/auth.provider";
import { FavListingsProvider } from "../contexts/fav-listings/favListings.provider";
import { AddListingProvider } from "../contexts/add-listing/addListing.provider";
import { EditListingProvider } from "../contexts/edit-listing/editListing.provider";
import { AppProvider } from "../contexts/app/app.provider";
import AppLayout from "../containers/layouts/AppLayout";
import { getClientCookie, setLoginCookies } from "../lib/cookiesHelpers";
import ErrorBoundary from "../containers/ErrorBoundary";
import "../components/listing-location/overrides.scss";
import "../styles/applePay.css";
import "../styles/globals.css";
import { initializeGA4 } from "../lib/tracking/gaHelpers";
import Config from "../common/config";
import { events } from "../lib/tracking/constants";

const Modal = dynamic(() => import("../components/modal/Modal"), {
  ssr: false,
});

function App({ Component, pageProps }) {
  const [modalProps, setModalProps] = useState({});

  const handleError = () => {
    const errorProps = {
      visible: true,
      okLabel: "تسجيل الدخول",
      closeOnClickOutside: false,
      closeBtn: true,
      headerIcon: "Warning-colored",
      children: <div>انتهت مدة الجلسة، الرجاء إعادة تسجيل الدخول.</div>,
      onOk: () => {
        setLoginCookies("", {});
        window.location.href = `/login?returnTo=${encodeURIComponent(
          window.location.pathname
        )}`;
      },
      onCancel: () => {
        setLoginCookies("", {});
        window.location.reload();
      },
    };
    setModalProps(errorProps);
  };

  const apolloClient = useApollo(pageProps, handleError);

  const appProviderValue = {
    isMapPage: pageProps.isMapPage,
    isDailyRentPage: pageProps.isDailyRentPage,
  };

  const setUpGA4Tracking = (initialData) => {
    const gtmContainerId = Config.GA4_TRACKING_ID;
    if (typeof window !== "undefined") {
      const gtmInitListener = () => initializeGA4(initialData, gtmContainerId);
      window.addEventListener("load", gtmInitListener);
    }
  };

  useEffect(() => {
    let user;
    if (getClientCookie("webapp_token")) {
      user = {
        event: events.USER_LOADED,
        user_phone: getClientCookie("user_phone"),
        user_name: getClientCookie("user_name"),
        user_id: getClientCookie("user_id"),
        user_img: getClientCookie("user_img"),
        email: getClientCookie("user_email"),
        user_email: getClientCookie("user_about"),
        user_chosen_user_type: getClientCookie("user_chosen_user_type"),
        user_iam_verified: getClientCookie("user_iam_verified"),
        user_listings: getClientCookie("user_listings"),
        user_favs: getClientCookie("user_favs"),
        user_type: getClientCookie("user_type"),
        user_aqar_plus_active: getClientCookie("user_active_aqar_plus_user"),
        user_paid: getClientCookie("user_paid"),
        user_create_time: getClientCookie("user_create_time"),
      };
    }
    setUpGA4Tracking(user);
  }, []);

  return (
    <AppProvider value={appProviderValue}>
      <ApolloProvider client={apolloClient}>
        <AuthProvider>
          <RegionsProvider>
            <ListingListProvider>
              <FavListingsProvider>
                <AddListingProvider>
                  <EditListingProvider>
                    <AhliCalculatorProvider>
                      <AppLayout
                        type={pageProps.layoutType}
                        isDailyRentPage={pageProps.isDailyRentPage}
                      >
                        <Modal {...modalProps} />
                        <ErrorBoundary>
                          <Component {...pageProps} />
                        </ErrorBoundary>
                      </AppLayout>
                    </AhliCalculatorProvider>
                  </EditListingProvider>
                </AddListingProvider>
              </FavListingsProvider>
            </ListingListProvider>
          </RegionsProvider>
        </AuthProvider>
      </ApolloProvider>
    </AppProvider>
  );
}

export default App;
